import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguages } from '@/hooks/useLanguages';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const buttonLabelKeys = [
  { key: 'enter_number', label: 'Enter Number Button', section: 'Exhibit Access' },
  { key: 'scan_qr', label: 'Scan QR Button', section: 'Exhibit Access' },
  { key: 'favorites', label: 'Favorites Button', section: 'Exhibit Access' },
  { key: 'browse_tours', label: 'Browse Tours Button', section: 'Exhibit Access' },
  { key: 'visitor_info_title', label: 'Visitor Info Title', section: 'Visitor Information' },
  { key: 'visitor_info_subtitle', label: 'Visitor Info Subtitle', section: 'Visitor Information' },
  { key: 'available_tours', label: 'Available Tours Section', section: 'Tours' },
  { key: 'view_all_tours', label: 'View All Tours Link', section: 'Tours' },
  { key: 'home', label: 'Home Navigation', section: 'Bottom Navigation' },
  { key: 'tours', label: 'Tours Navigation', section: 'Bottom Navigation' },
  { key: 'keypad', label: 'Keypad Navigation', section: 'Bottom Navigation' },
  { key: 'info', label: 'Info Navigation', section: 'Bottom Navigation' }
];

interface ButtonLabelsEditorProps {
  buttonLabels: Record<string, Record<string, string>>;
  saving: boolean;
  onUpdateLabels: (labels: Record<string, Record<string, string>>) => Promise<void>;
}

export function ButtonLabelsEditor({ buttonLabels, saving, onUpdateLabels }: ButtonLabelsEditorProps) {
  const { languages } = useLanguages();
  const [editedLabels, setEditedLabels] = useState(buttonLabels);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset local state when props change (after saving)
  React.useEffect(() => {
    setEditedLabels(buttonLabels);
    setHasChanges(false);
  }, [buttonLabels]);

  const handleLabelChange = (languageCode: string, key: string, value: string) => {
    const newLabels = {
      ...editedLabels,
      [languageCode]: {
        ...editedLabels[languageCode],
        [key]: value
      }
    };
    setEditedLabels(newLabels);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onUpdateLabels(editedLabels);
    setHasChanges(false);
  };

  const handleReset = () => {
    setEditedLabels(buttonLabels);
    setHasChanges(false);
  };

  const groupedLabels = buttonLabelKeys.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {} as Record<string, typeof buttonLabelKeys>);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Button Labels</CardTitle>
        <CardDescription>
          Customize the text displayed on buttons and sections throughout the home page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue={languages[0]?.code} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${Math.min(languages.length, 5)}, minmax(0, 1fr))` }}>
            {languages.map(lang => (
              <TabsTrigger key={lang.code} value={lang.code}>
                {lang.name}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {languages.map(lang => (
            <TabsContent key={lang.code} value={lang.code} className="space-y-6">
              {Object.entries(groupedLabels).map(([section, items]) => (
                <div key={section} className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">{section}</h4>
                  <div className="grid gap-4">
                    {items.map(item => (
                      <div key={item.key} className="space-y-2">
                        <Label htmlFor={`${lang.code}-${item.key}`}>
                          {item.label}
                        </Label>
                        <Input
                          id={`${lang.code}-${item.key}`}
                          value={editedLabels[lang.code]?.[item.key] || ''}
                          onChange={(e) => handleLabelChange(lang.code, item.key, e.target.value)}
                          placeholder={`Enter ${item.label.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>

        {hasChanges && (
          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}